/**
 * HELPER PARA DOWNLOADS DE ARQUIVOS
 * 
 * Este arquivo contém funções para fazer download de arquivos
 * que funcionam tanto no browser quanto no React Native
 */

import { isReactNativeWebView, downloadFileRN } from './useWebViewBridge';

/**
 * CONVERTE STRING BASE64 PARA BLOB
 * 
 * Parâmetros:
 * - base64: String base64 do arquivo
 * - contentType: Tipo MIME do arquivo (ex: "application/pdf")
 * 
 * O que faz:
 * - Decodifica a string base64
 * - Converte para bytes
 * - Cria um Blob com o tipo MIME correto
 * 
 * Por que é necessário:
 * - O browser precisa de um Blob para fazer download
 * - O base64 vem do servidor, mas o download precisa ser um arquivo
 */
export const base64ToBlob = (base64: string, contentType = ''): Blob => {
  const byteCharacters = atob(base64);
  const byteArrays = [];

  // Processa o base64 em chunks de 512 bytes
  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);
    const byteNumbers = new Array(slice.length);
    
    // Converte cada caractere para byte
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  return new Blob(byteArrays, { type: contentType });
};

/**
 * FAZ DOWNLOAD DE ARQUIVO - FUNCIONA EM AMBOS OS AMBIENTES
 * 
 * Parâmetros:
 * - base64Data: Dados do arquivo em base64
 * - fileName: Nome do arquivo (ex: "documento.pdf")
 * - contentType: Tipo MIME (ex: "application/pdf")
 * 
 * O que faz:
 * 1. Verifica se está no React Native WebView
 * 2. Se SIM: Envia para o React Native fazer download nativo
 * 3. Se NÃO: Usa método tradicional do browser
 * 
 * Método React Native:
 * - Envia mensagem para o React Native
 * - React Native salva na pasta Downloads do sistema
 * - Usuário recebe notificação nativa
 * 
 * Método Browser:
 * - Cria um Blob com os dados
 * - Cria um link temporário
 * - Simula clique para fazer download
 * - Limpa a memória após download
 */
export const downloadFile = (
  base64Data: string,
  fileName: string,
  contentType: string = 'application/pdf'
): void => {
  try {
    // Se está no React Native WebView, usa a bridge nativa
    if (isReactNativeWebView()) {
      downloadFileRN(base64Data, fileName, contentType);
      return;
    }

    // Caso contrário, usa a abordagem padrão do browser
    const blob = base64ToBlob(base64Data, contentType);
    const fileUrl = URL.createObjectURL(blob);
    
    // Cria link temporário para download
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName;
    link.click();
    
    // Libera a memória após um pequeno delay
    setTimeout(() => {
      URL.revokeObjectURL(fileUrl);
    }, 100);
    
  } catch (error) {
    console.error('[Download] Erro ao fazer download:', error);
    throw error;
  }
};
